import { IEquipment } from '@frontend/equipment/types';
import { IProduct, ProductType } from '@frontend/product/types';
import { Stock } from '@frontend/stock/types';

export interface KitEntry {
    product: IProduct;
    quantity: number;
}

export interface SlotSelection {
    spot_id: string;
    module_id: string;
    slot_id: string;
    /** Kit child product ids present in this slot. */
    product_ids: string[];
    /** Equipment instances in this slot that cover equipment children. */
    equipments: IEquipment[];
    /** Consumable stock rows in this slot that cover consumable children. */
    consumableStocks: Stock[];
}

type CandidateSlot = {
    key: string;
    spot_id: string;
    module_id: string;
    slot_id: string;
    /** Full kit-child quantity contributed when this SLOT is opened. */
    quantities: Map<string, number>;
    equipments: IEquipment[];
    consumableStocks: Stock[];
    product_ids: string[];
};

type SlotBucket = { equipments: Map<string, IEquipment[]>; consumables: Map<string, Stock[]> };

const slotKey = (s: { spot_id: string; module_id: string; slot_id: string }) => `${s.spot_id}\u0000${s.module_id}\u0000${s.slot_id}`;
const parseSlotKey = (key: string): [string, string, string] => {
    const [spot_id, module_id, slot_id] = key.split('\u0000');
    return [spot_id, module_id, slot_id];
};

const buildRequired = (entries: KitEntry[]): Map<string, number> => {
    const required = new Map<string, number>();
    for (const entry of entries) {
        if (entry.product.type !== ProductType.EQUIPMENT && entry.product.type !== ProductType.CONSUMABLE) continue;
        if (entry.quantity <= 0) continue;
        required.set(entry.product.id, (required.get(entry.product.id) ?? 0) + entry.quantity);
    }
    return required;
};

const buildSlotBuckets = (input: {
    required: Map<string, number>;
    equipments: IEquipment[];
    equipmentStocks: Stock[];
    consumableStocks: Stock[];
}): Map<string, SlotBucket> => {
    const { required, equipments, equipmentStocks, consumableStocks } = input;
    const equipmentById = new Map(equipments.map((e) => [e.id, e]));
    const slotMap = new Map<string, SlotBucket>();

    for (const stock of equipmentStocks) {
        if (!stock.equipment_id) continue;
        const equipment = equipmentById.get(stock.equipment_id);
        if (!equipment || !required.has(equipment.product_id)) continue;
        const key = slotKey(stock);
        const bucket = slotMap.get(key) ?? { equipments: new Map(), consumables: new Map() };
        const list = bucket.equipments.get(equipment.product_id) ?? [];
        list.push(equipment);
        bucket.equipments.set(equipment.product_id, list);
        slotMap.set(key, bucket);
    }

    for (const stock of consumableStocks) {
        if (stock.quantity <= 0 || !required.has(stock.product_id)) continue;
        const key = slotKey(stock);
        const bucket = slotMap.get(key) ?? { equipments: new Map(), consumables: new Map() };
        const list = bucket.consumables.get(stock.product_id) ?? [];
        list.push(stock);
        bucket.consumables.set(stock.product_id, list);
        slotMap.set(key, bucket);
    }

    return slotMap;
};

const toCandidate = (key: string, bucket: SlotBucket): CandidateSlot => {
    const [spot_id, module_id, slot_id] = parseSlotKey(key);
    const quantities = new Map<string, number>();
    const equipments: IEquipment[] = [];
    const consumableStocks: Stock[] = [];
    const product_ids: string[] = [];

    for (const [productId, items] of bucket.equipments) {
        quantities.set(productId, (quantities.get(productId) ?? 0) + items.length);
        equipments.push(...items);
        product_ids.push(productId);
    }
    for (const [productId, stocks] of bucket.consumables) {
        const qty = stocks.reduce((sum, stock) => sum + stock.quantity, 0);
        quantities.set(productId, (quantities.get(productId) ?? 0) + qty);
        consumableStocks.push(...stocks);
        if (!product_ids.includes(productId)) product_ids.push(productId);
    }

    return { key, spot_id, module_id, slot_id, quantities, equipments, consumableStocks, product_ids };
};

/** True when opening this SLOT would not exceed any remaining kit quantity. */
const fitsRemaining = (slot: CandidateSlot, remaining: Map<string, number>): boolean => {
    for (const [productId, qty] of slot.quantities) {
        if (qty > (remaining.get(productId) ?? 0)) return false;
    }
    return true;
};

const subtractSlot = (remaining: Map<string, number>, slot: CandidateSlot): Map<string, number> => {
    const next = new Map(remaining);
    for (const [productId, qty] of slot.quantities) {
        const left = (next.get(productId) ?? 0) - qty;
        if (left <= 0) next.delete(productId);
        else next.set(productId, left);
    }
    return next;
};

const toSelection = (slot: CandidateSlot): SlotSelection => ({
    spot_id: slot.spot_id,
    module_id: slot.module_id,
    slot_id: slot.slot_id,
    product_ids: slot.product_ids,
    equipments: slot.equipments,
    consumableStocks: slot.consumableStocks
});

const coverKeys = (slots: CandidateSlot[]) => slots.map((s) => s.key).join('\n');

/**
 * Exact whole-SLOT cover for kit entries:
 *
 * Opening a SLOT contributes **all** of its kit-child stock. A valid selection
 * is a subset of SLOTs whose quantities sum exactly to each required quantity
 * (no overshoot / "extras", no undershoot). When multiple covers exist, prefer
 * fewer SLOTs; break ties by stable slot key order.
 */
export const computeSlotCover = (input: {
    entries: KitEntry[];
    equipments: IEquipment[];
    equipmentStocks: Stock[];
    consumableStocks: Stock[];
}): SlotSelection[] => {
    const { entries, equipments, equipmentStocks, consumableStocks } = input;
    if (entries.length === 0) return [];

    const required = buildRequired(entries);
    if (required.size === 0) return [];

    const slotMap = buildSlotBuckets({ required, equipments, equipmentStocks, consumableStocks });
    const candidates = [...slotMap.entries()]
        .map(([key, bucket]) => toCandidate(key, bucket))
        .filter((slot) => fitsRemaining(slot, required))
        .sort((a, b) => a.key.localeCompare(b.key));

    // Object holder so TypeScript tracks mutations inside the nested search closure.
    const state: { best: CandidateSlot[] | null } = { best: null };

    const search = (index: number, chosen: CandidateSlot[], remaining: Map<string, number>) => {
        if (remaining.size === 0) {
            const current = state.best;
            if (!current || chosen.length < current.length || (chosen.length === current.length && coverKeys(chosen) < coverKeys(current))) {
                state.best = [...chosen];
            }
            return;
        }
        if (index >= candidates.length) return;
        if (state.best && chosen.length >= state.best.length) return;

        const slot = candidates[index];
        if (fitsRemaining(slot, remaining)) {
            chosen.push(slot);
            search(index + 1, chosen, subtractSlot(remaining, slot));
            chosen.pop();
            if (state.best && chosen.length >= state.best.length) return;
        }
        search(index + 1, chosen, remaining);
    };

    search(0, [], new Map(required));
    return state.best ? state.best.map(toSelection) : [];
};

/**
 * Smallest and largest kit-copy counts `k` for which an exact whole-SLOT cover
 * exists. `stockBound` is an upper bound (e.g. floor of spot-wide availability).
 * Some values between min and max may still lack a cover.
 */
export const computeExactCoverAmountBounds = (input: {
    entries: KitEntry[];
    equipments: IEquipment[];
    equipmentStocks: Stock[];
    consumableStocks: Stock[];
    stockBound: number;
}): { min: number; max: number } => {
    const { entries, stockBound } = input;
    if (entries.length === 0 || stockBound < 1) return { min: 0, max: 0 };

    let min = 0;
    let max = 0;
    for (let k = 1; k <= stockBound; k++) {
        const scaled = entries.map((e) => ({ product: e.product, quantity: e.quantity * k }));
        if (computeSlotCover({ ...input, entries: scaled }).length === 0) continue;
        if (min === 0) min = k;
        max = k;
    }
    return { min, max };
};

/** True when an exact whole-SLOT cover exists for the given kit-copy count. */
export const hasExactCoverForAmount = (input: {
    entries: KitEntry[];
    equipments: IEquipment[];
    equipmentStocks: Stock[];
    consumableStocks: Stock[];
    amount: number;
}): boolean => {
    const { entries, amount } = input;
    if (entries.length === 0 || amount < 1) return false;
    const scaled = entries.map((e) => ({ product: e.product, quantity: e.quantity * amount }));
    return computeSlotCover({ ...input, entries: scaled }).length > 0;
};

/**
 * Returns the kit entries whose required quantity is not fully covered by the
 * provided slot selection.
 */
export const computeMissingProducts = (entries: KitEntry[], slots: SlotSelection[]): IProduct[] => {
    const satisfiedEquipment = new Map<string, number>();
    const satisfiedConsumable = new Map<string, number>();
    for (const slot of slots) {
        for (const equipment of slot.equipments) {
            satisfiedEquipment.set(equipment.product_id, (satisfiedEquipment.get(equipment.product_id) ?? 0) + 1);
        }
        for (const stock of slot.consumableStocks) {
            satisfiedConsumable.set(stock.product_id, (satisfiedConsumable.get(stock.product_id) ?? 0) + stock.quantity);
        }
    }

    const missing: IProduct[] = [];
    for (const entry of entries) {
        const satisfied =
            entry.product.type === ProductType.EQUIPMENT
                ? (satisfiedEquipment.get(entry.product.id) ?? 0)
                : entry.product.type === ProductType.CONSUMABLE
                  ? (satisfiedConsumable.get(entry.product.id) ?? 0)
                  : 0;
        if (satisfied < entry.quantity) missing.push(entry.product);
    }
    return missing;
};

/** Consumable quantity taken from a selected SLOT for a given product. */
export const consumableQuantityInSlot = (slot: SlotSelection, productId: string): number =>
    slot.consumableStocks.filter((s) => s.product_id === productId).reduce((sum, s) => sum + s.quantity, 0);
